import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import HeaderMain from "./Components/HeaderMain";
import axios from "axios";

const API_URL = process.env.REACT_APP_BASE_URL;

const TransactionHistory = () => {
  const [userId, setUserId] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userBalance, setUserBalance] = useState(0);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterType, setFilterType] = useState("all"); // all, credit, debit
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (!user?.uid) return;
    setUserId(user.uid);
    fetchUserBalance(user.uid);
    fetchTransactions(user.uid, 1);
  }, [user?.uid]);

  const fetchUserBalance = async (userId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/accounting/balance/${userId}`,
        { withCredentials: true }
      );
      setUserBalance(response.data.balance);
    } catch (error) {
      console.error("Error fetching balance:", error);
    }
  };

  const fetchTransactions = async (userId, page = 1) => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/accounting/transactions/${userId}?page=${page}&limit=20`,
        { withCredentials: true }
      );
      const transactionData = Array.isArray(response.data) ? response.data : [];

      // Apply client-side filtering
      let filteredTransactions = transactionData;
      if (filterType !== "all") {
        filteredTransactions = transactionData.filter(
          (t) => t.type === filterType
        );
      }
      if (searchTerm) {
        filteredTransactions = filteredTransactions.filter(
          (t) =>
            t.description &&
            t.description.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      setTransactions(filteredTransactions);
      setTotalPages(Math.ceil(filteredTransactions.length / 20));
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setTransactions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilter) => {
    setFilterType(newFilter);
    setCurrentPage(1);
    fetchTransactions(userId, 1);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.length >= 2 || value.length === 0) {
      setCurrentPage(1);
      fetchTransactions(userId, 1);
    }
  };

  const handlePageChange = (newPage) => {
    fetchTransactions(userId, newPage);
  };

  const handleTransactionDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setShowDetailsModal(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="App">
      <HeaderMain pageType="transaction-history" />

      <main className="max-w-7xl mx-auto p-5 sm:p-4 md:p-5 min-h-screen">
        <div className="mb-6 sm:mb-8">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 sm:p-5 md:p-6 rounded-xl shadow-lg flex flex-col md:flex-row justify-between items-center gap-3 sm:gap-4">
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold m-0">💰 Transaction History</h1>
            <div className="flex flex-col items-center md:items-end gap-1">
              <span className="text-xs sm:text-sm opacity-90">Current Balance:</span>
              <span className="text-2xl sm:text-3xl font-bold">
                ${userBalance.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Controls Section */}
        <div className="bg-white p-3 sm:p-4 md:p-5 rounded-xl shadow-md mb-4 sm:mb-5 flex flex-col lg:flex-row gap-3 sm:gap-4 md:gap-5 items-stretch lg:items-center">
          <div className="flex-1 w-full lg:min-w-64">
            <input
              type="text"
              placeholder="Search by description..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full p-2 sm:p-2.5 md:p-3 border-2 border-gray-200 rounded-lg text-sm sm:text-base focus:outline-none focus:border-blue-500 focus:ring-2 sm:focus:ring-4 focus:ring-blue-100 transition-all duration-200"
            />
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            <span className="font-semibold text-gray-700 text-xs sm:text-sm text-center sm:text-left">Filter:</span>
            <div className="flex gap-2 justify-center sm:justify-start">
              <button
                className={`px-3 sm:px-4 py-1.5 sm:py-2 border-2 rounded-full font-semibold text-xs sm:text-sm cursor-pointer transition-all duration-200 ${
                  filterType === "all"
                    ? "bg-blue-600 border-blue-600 text-white"
                    : "border-gray-200 bg-white text-gray-700 hover:border-blue-600 hover:bg-gray-50"
                }`}
                onClick={() => handleFilterChange("all")}
              >
                All
              </button>
              <button
                className={`px-3 sm:px-4 py-1.5 sm:py-2 border-2 rounded-full font-semibold text-xs sm:text-sm cursor-pointer transition-all duration-200 ${
                  filterType === "credit"
                    ? "bg-blue-600 border-blue-600 text-white"
                    : "border-gray-200 bg-white text-gray-700 hover:border-blue-600 hover:bg-gray-50"
                }`}
                onClick={() => handleFilterChange("credit")}
              >
                💰 Received
              </button>
              <button
                className={`px-3 sm:px-4 py-1.5 sm:py-2 border-2 rounded-full font-semibold text-xs sm:text-sm cursor-pointer transition-all duration-200 ${
                  filterType === "debit"
                    ? "bg-blue-600 border-blue-600 text-white"
                    : "border-gray-200 bg-white text-gray-700 hover:border-blue-600 hover:bg-gray-50"
                }`}
                onClick={() => handleFilterChange("debit")}
              >
                💸 Sent
              </button>
            </div>
          </div>
        </div>

        {/* Transactions List */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12 sm:py-16 gap-4 sm:gap-5 px-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
              <p className="text-sm sm:text-base text-gray-600">Loading transactions...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 sm:py-16 text-center px-4">
              <div className="text-4xl sm:text-5xl md:text-6xl mb-4 sm:mb-5">📭</div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-700 mb-2">
                No transactions found
              </h3>
              <p className="text-sm sm:text-base text-gray-600">
                {searchTerm || filterType !== "all"
                  ? "Try adjusting your search or filter criteria."
                  : "You haven't made any transactions yet."}
              </p>
            </div>
          ) : (
            <>
              <div className="max-h-[400px] sm:max-h-[500px] md:max-h-[600px] overflow-y-auto">
                {transactions.map((transaction) => (
                  <div
                    key={transaction.id}
                    className={`border-b border-gray-100 p-3 sm:p-4 md:p-5 hover:bg-gray-50 transition-colors duration-200 last:border-b-0 ${transaction.type}`}
                  >
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 sm:gap-4">
                      <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-1 w-full">
                        <div
                          className={`text-2xl sm:text-3xl w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full flex-shrink-0 ${
                            transaction.type === "credit"
                              ? "bg-green-100"
                              : "bg-red-100"
                          }`}
                        >
                          {transaction.type === "credit" ? "💰" : "💸"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-gray-800 text-sm sm:text-base md:text-lg mb-1 break-words">
                            {transaction.other_party_type}{" "}
                            {transaction.other_party}
                          </div>
                          {transaction.other_party_wallet && transaction.other_party_wallet !== 'N/A' && (
                            <div className="font-medium text-blue-600 text-xs sm:text-sm mb-1 break-all">
                              💳 {transaction.other_party_wallet}
                            </div>
                          )}
                          {transaction.transaction_number && (
                            <div className="font-medium text-gray-500 text-xs mb-1 break-all">
                              📄 {transaction.transaction_number}
                            </div>
                          )}
                          <div className="font-medium text-gray-600 text-xs sm:text-sm mb-1">
                            {formatDateTime(transaction.created_at)}
                          </div>
                          {transaction.description && (
                            <div className="text-gray-500 text-xs sm:text-sm italic mb-1 break-words">
                              {transaction.description}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row lg:flex-col items-start sm:items-center lg:items-end gap-2 w-full sm:w-auto lg:w-auto text-left sm:text-right lg:text-right">
                        <div
                          className={`font-bold text-lg sm:text-xl ${
                            transaction.type === "credit"
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          {transaction.type === "credit" ? "+" : "-"}$
                          {transaction.amount.toFixed(2)}
                        </div>
                        <div className="font-medium text-gray-600 text-xs sm:text-sm">
                          Balance: ${transaction.new_balance.toFixed(2)}
                        </div>
                        <button
                          onClick={() => handleTransactionDetails(transaction)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold transition-colors duration-200 w-full sm:w-auto"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-5 p-3 sm:p-4 md:p-5 bg-gray-50 border-t border-gray-200">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 sm:px-5 py-1.5 sm:py-2 rounded-lg text-sm font-semibold transition-all duration-200 w-full sm:w-auto"
                  >
                    ← Previous
                  </button>

                  <div className="font-semibold text-gray-700 text-xs sm:text-sm">
                    Page {currentPage} of {totalPages}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 sm:px-5 py-1.5 sm:py-2 rounded-lg text-sm font-semibold transition-all duration-200 w-full sm:w-auto"
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Transaction Details Modal */}
        {showDetailsModal && selectedTransaction && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl max-w-lg w-full max-h-[85vh] sm:max-h-[80vh] overflow-y-auto shadow-2xl transform transition-all duration-300 scale-100">
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4 sm:p-5 md:p-6 rounded-t-xl flex justify-between items-center sticky top-0 z-10">
                <h3 className="m-0 font-bold text-base sm:text-lg md:text-xl tracking-wide">
                  💰 Transaction Details
                </h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 border-none text-white text-lg sm:text-xl cursor-pointer p-1.5 sm:p-2 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 flex-shrink-0"
                >
                  ×
                </button>
              </div>

              <div className="p-4 sm:p-5 md:p-6">
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 sm:gap-0 p-3 sm:p-4 bg-gray-50 rounded-lg">
                    <span className="font-semibold text-gray-600 text-sm sm:text-base">Type:</span>
                    <span
                      className={`font-bold px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm w-fit ${
                        selectedTransaction.type === "credit"
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {selectedTransaction.type === "credit"
                        ? "+ Received"
                        : "- Sent"}
                    </span>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 sm:gap-0 p-3 sm:p-4 bg-gray-50 rounded-lg">
                    <span className="font-semibold text-gray-600 text-sm sm:text-base">Amount:</span>
                    <span
                      className={`font-bold text-lg sm:text-xl ${
                        selectedTransaction.type === "credit"
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {selectedTransaction.type === "credit" ? "+" : "-"}$
                      {selectedTransaction.amount.toFixed(2)}
                    </span>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 sm:gap-0 p-3 sm:p-4 bg-gray-50 rounded-lg">
                    <span className="font-semibold text-gray-600 text-sm sm:text-base">
                      {selectedTransaction.other_party_type}:
                    </span>
                    <div className="text-left sm:text-right">
                      <span className="font-bold text-gray-800 block text-sm sm:text-base break-words">
                        {selectedTransaction.other_party}
                      </span>
                      {selectedTransaction.other_party_wallet && selectedTransaction.other_party_wallet !== 'N/A' && (
                        <span className="font-medium text-blue-600 text-xs sm:text-sm break-all">
                          💳 {selectedTransaction.other_party_wallet}
                        </span>
                      )}
                    </div>
                  </div>

                  {selectedTransaction.transaction_number && (
                    <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 sm:gap-0 p-3 sm:p-4 bg-gray-50 rounded-lg">
                      <span className="font-semibold text-gray-600 text-sm sm:text-base">Transaction ID:</span>
                      <span className="font-mono text-gray-800 text-xs sm:text-sm break-all">{selectedTransaction.transaction_number}</span>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 sm:gap-0 p-3 sm:p-4 bg-gray-50 rounded-lg">
                    <span className="font-semibold text-gray-600 text-sm sm:text-base">
                      Date & Time:
                    </span>
                    <span className="text-gray-800 text-xs sm:text-sm">
                      {formatDateTime(selectedTransaction.created_at)}
                    </span>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 sm:gap-0 p-3 sm:p-4 bg-gray-50 rounded-lg">
                    <span className="font-semibold text-gray-600 text-sm sm:text-base">
                      Balance Before:
                    </span>
                    <span className="font-mono text-gray-800 text-sm sm:text-base">
                      ${selectedTransaction.prev_balance.toFixed(2)}
                    </span>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 sm:gap-0 p-3 sm:p-4 bg-gray-50 rounded-lg">
                    <span className="font-semibold text-gray-600 text-sm sm:text-base">
                      Balance After:
                    </span>
                    <span className="font-mono text-gray-800 text-sm sm:text-base">
                      ${selectedTransaction.new_balance.toFixed(2)}
                    </span>
                  </div>

                  {selectedTransaction.description && (
                    <div className="p-3 sm:p-4 bg-gray-50 rounded-lg">
                      <span className="font-semibold text-gray-600 block mb-2 text-sm sm:text-base">
                        Description:
                      </span>
                      <span className="text-gray-800 text-xs sm:text-sm break-words">
                        {selectedTransaction.description}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex justify-end mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl text-sm sm:text-base font-semibold hover:from-purple-700 hover:to-purple-800 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-purple-300 w-full sm:w-auto"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default TransactionHistory;
